import {
  memo,
  type RefObject,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import { type MeetingAudioEnergyLevel } from '@/features/meetings/meetings.types';
import { cn } from '@/lib/utils';

const IDLE_RATIOS = [0.35, 0.8, 0.5];
const BAR_PEAK_SCALES = [0.55, 0.85, 0.55];

/**
 * Three-bar audio wave visualizer with two display modes:
 *
 * Idle mode (idle=true):
 *   Decorative -- bars render at proportional heights (IDLE_RATIOS) with no
 *   dynamic updates. Used when a meeting exists but is not recording.
 *
 * Active mode (idle=false, default):
 *   Bars reflect real audio energy levels via updateWaveRef. Floor is 3px.
 *   useAudioWaveSilenceDetection (exported below) adds silence detection:
 *     - Bars hold idle appearance until the first real audio signal arrives.
 *     - After 2s of silence, bars drop to 3px and color transitions to red.
 *     - Audio resuming immediately reverts to green and responsive bars.
 *
 * All height/color changes animate via CSS transition-all (200ms).
 */
export const AnimatedAudiowave = memo(
  ({
    updateWaveRef,
    className,
    maxLength = 36,
    barColor = 'bg-foreground-subtle',
    gap = '0.1rem',
    barWidth = '0.15rem',
    idle = false,
  }: {
    updateWaveRef?: RefObject<(levels: MeetingAudioEnergyLevel) => void>;
    className?: string;
    maxLength?: number;
    barColor?: string;
    gap?: number | string;
    orientation?: 'horizontal' | 'vertical';
    barWidth?: string | number;
    /** When true, bars render at decorative proportional heights instead of flat 3px */
    idle?: boolean;
  }) => {
    const ref1 = useRef<HTMLDivElement>(null);
    const ref2 = useRef<HTMLDivElement>(null);
    const ref3 = useRef<HTMLDivElement>(null);
    const allRefs = [ref1, ref2, ref3];
    const minLength = idle ? undefined : 3;

    const updateWave = (levels: MeetingAudioEnergyLevel) => {
      if (idle) return;
      if (!ref1.current || !ref2.current || !ref3.current) return;

      const values = [levels[0] || 0, levels[1] || 0, levels[2] || 0];

      for (let i = 0; i < allRefs.length; i++) {
        const barLength =
          Math.floor(values[i] * BAR_PEAK_SCALES[i] * (maxLength - 3)) + 3;
        const ref = allRefs[i];
        if (ref.current) ref.current.style.height = `${barLength}px`;
      }
    };

    useEffect(() => {
      if (!idle) return;
      const refs = [ref1, ref2, ref3];
      for (const ref of refs) {
        if (ref.current) ref.current.style.height = '';
      }
    }, [idle, ref1, ref2, ref3]);

    useImperativeHandle(updateWaveRef, () => updateWave);

    return (
      <div
        className={cn('flex items-center justify-center', className)}
        style={{ gap, width: `${maxLength}px`, maxWidth: `${maxLength}px` }}
      >
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            ref={allRefs[i]}
            className={cn('rounded-full transition-all duration-200', barColor)}
            style={{
              width: barWidth,
              minHeight: minLength
                ? `${minLength}px`
                : `${Math.floor(maxLength * IDLE_RATIOS[i])}px`,
            }}
          />
        ))}
      </div>
    );
  }
);

// --- Silence detection hook ---

export const SILENCE_TIMEOUT_MS = 2_000;
export const AUDIO_THRESHOLD = 0.03;
export const SILENT_BAR_COLOR = 'bg-red-500 [.system-dark_&]:bg-red-400';

/**
 * Hook that wraps an audio energy subscription with silence detection.
 * Returns a ref to drive AnimatedAudiowave and the resolved bar color
 * (switches to red after SILENCE_TIMEOUT_MS of silence).
 *
 * Silence clock starts from first energy event, not from mount --
 * avoids false red during recording startup latency.
 * Resets all state when subscribeEnergyLevel identity changes
 * (e.g. recording stop/resume).
 */
export function useAudioWaveSilenceDetection(
  subscribeEnergyLevel: (
    cb: (levels: MeetingAudioEnergyLevel) => void
  ) => () => void,
  activeBarColor?: string
) {
  const updateWaveRef = useRef<(levels: MeetingAudioEnergyLevel) => void>(
    () => null
  );
  const lastEventAt = useRef(0);
  const hasReceivedAudio = useRef(false);
  const isSilentRef = useRef(false);
  const silenceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isSilent, setIsSilent] = useState(false);

  useEffect(() => {
    if (silenceTimeoutRef.current) {
      clearTimeout(silenceTimeoutRef.current);
      silenceTimeoutRef.current = null;
    }
    lastEventAt.current = 0;
    hasReceivedAudio.current = false;
    isSilentRef.current = false;
    setIsSilent(false);

    const unsubscribe = subscribeEnergyLevel((newLevels) => {
      const now = Date.now();
      if (!lastEventAt.current) {
        lastEventAt.current = now;
        silenceTimeoutRef.current = setTimeout(() => {
          if (!isSilentRef.current) {
            isSilentRef.current = true;
            setIsSilent(true);
            updateWaveRef.current?.([0, 0, 0, 0, 0]);
          }
          silenceTimeoutRef.current = null;
        }, SILENCE_TIMEOUT_MS);
      }

      const hasAudio = newLevels.some((l) => l > AUDIO_THRESHOLD);
      if (hasAudio) {
        hasReceivedAudio.current = true;
        lastEventAt.current = now;
        if (silenceTimeoutRef.current) {
          clearTimeout(silenceTimeoutRef.current);
        }
        silenceTimeoutRef.current = setTimeout(() => {
          if (!isSilentRef.current) {
            isSilentRef.current = true;
            setIsSilent(true);
            updateWaveRef.current?.([0, 0, 0, 0, 0]);
          }
          silenceTimeoutRef.current = null;
        }, SILENCE_TIMEOUT_MS);
        if (isSilentRef.current) {
          isSilentRef.current = false;
          setIsSilent(false);
        }
      }
      if (hasReceivedAudio.current) {
        updateWaveRef.current?.(newLevels);
      }
    });
    return () => {
      if (silenceTimeoutRef.current) {
        clearTimeout(silenceTimeoutRef.current);
        silenceTimeoutRef.current = null;
      }
      unsubscribe();
    };
  }, [subscribeEnergyLevel]);

  const barColor = isSilent ? SILENT_BAR_COLOR : activeBarColor;

  return { updateWaveRef, barColor, isSilent };
}
